import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { getCurrencyConfig } from '@app/helpers/currency-format.helper';
import { Income } from '@app/models/account.model';
import { knowledgeTestFormGroup } from '@app/pages/investor-qualification-wizard/investor-qualification-wizard-form.helper';
import { AccountService } from '@app/services/account.service';
import { AuthStore } from '@app/stores/auth.store';
import { GenericStore } from '@app/stores/generic.store';
import { ValidatedCheckboxComponent } from '@app/ui-components/validated-checkbox/validated-checkbox.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, merge } from 'rxjs';
import { AppCurrencyPipe } from '../../../+pipes/currency.pipe';
import { GetLocalizedPipe } from '../../../+pipes/get-localized.pipe';
import { ValidatedCurrencyInputComponent } from '../../common/validated-currency-input/validated-currency-input.component';
import { IncomeTableComponent } from './income-table/income-table.component';
import { addFormGroup } from './knowledge-test-form.helper';
import { IncomeTable } from './knowledge-test.models';

@Component({
  selector: 'app-knowledge-test-form',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    IncomeTableComponent,
    ValidatedCheckboxComponent,
    ValidatedCurrencyInputComponent,
    ValidatedDropdownComponent,
    GetLocalizedPipe,
    AppCurrencyPipe,
  ],
  templateUrl: './knowledge-test-form.component.html',
  styleUrl: './knowledge-test-form.component.scss',
})
export class KnowledgeTestFormComponent implements OnInit {
  formGroup = input.required<FormGroup<knowledgeTestFormGroup>>();
  incomeSum: number = 0;

  protected authStore = inject(AuthStore);
  protected store = inject(GenericStore);
  selectedCurrency: FormControl<string>;

  currentNetWealth = signal(0);

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
  ) {
    this.selectedCurrency = fb.nonNullable.control('HUF');
  }

  ngOnInit(): void {
    const controls = this.formGroup().controls;
    this.selectedCurrency.setValue(
      this.authStore.currentUser()?.currencyCode ?? 'HUF',
    );

    merge(
      controls.incomes.valueChanges,
      controls.availableFounds.valueChanges,
      controls.financialCommitmentsValue.valueChanges,
      this.selectedCurrency.valueChanges,
    )
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.updateKnowledgeTest();
      });
  }
  createNewFileGroup(formArray: FormArray<FormGroup<IncomeTable>>): void {
    addFormGroup(formArray, this.fb);
  }

  getCurrencyConfig = getCurrencyConfig;

  private updateKnowledgeTest(): void {
    const controls = this.formGroup().controls;
    const incomesValue = controls.incomes.value;
    const availableFoundsValue = controls.availableFounds.value;
    const financialCommitmentsValue = controls.financialCommitmentsValue.value;

    this.incomeSum =
      incomesValue
        ?.map((value) =>
          !!value.netIncome && value.regularIncome ? value.netIncome : 0,
        )
        .reduce((sum, current) => sum + current, 0) ?? 0;

    this.accountService
      .setKnowledgeTest({
        incomes: incomesValue.map(
          (control) =>
            ({
              amount: control.netIncome,
              regulary: control.regularIncome,
            }) as Income,
        ),
        availableMoneyAssetsValue: availableFoundsValue ?? 0,
        financialCommitmentsValue: financialCommitmentsValue ?? 0,
        currencyCode: this.selectedCurrency.value,
      })
      .subscribe((response) =>
        this.currentNetWealth.set(response.notQualifiedUserNetWorth ?? 0),
      );
  }
}
